import { useState } from "react";
import { Link } from "react-router-dom";
import { buildUrl, useApi, useDebounced } from "../api";
import type { Paged, Poll } from "../api";
import {
  Empty,
  ErrorNote,
  Initiators,
  Loading,
  Pager,
  formatDate,
} from "../components";

const LIMIT = 25;

export default function Polls() {
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const search = useDebounced(query);

  const { data, loading, error, reload } = useApi<Paged<Poll>>(
    buildUrl("/api/polls", { q: search || null, limit: LIMIT, offset }),
  );

  function onSearch(value: string) {
    setQuery(value);
    setOffset(0);
  }

  return (
    <>
      <div className="page-head">
        <h1>Namentliche Abstimmungen</h1>
        <p>
          Jede namentliche Abstimmung des Bundestages mit dem Stimmverhalten aller
          Fraktionen. Wo möglich ist die zugrunde liegende Vorlage verknüpft — ohne
          sie bleibt das Abstimmungsverhalten ohne Inhalt.
        </p>
      </div>

      <div className="filters">
        <input
          type="search"
          placeholder="Suche im Titel, z. B. Tempolimit oder Stromsteuer"
          value={query}
          onChange={(e) => onSearch(e.target.value)}
          aria-label="Abstimmungen durchsuchen"
        />
      </div>

      {error ? (
        <ErrorNote message={error} onRetry={reload} />
      ) : loading && !data ? (
        <Loading rows={8} />
      ) : !data || data.items.length === 0 ? (
        <Empty>
          {search
            ? `Keine Abstimmung gefunden für „${search}“.`
            : "Noch keine Abstimmungen geladen."}
        </Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "6.5rem" }}>Datum</th>
                  <th>Gegenstand</th>
                  <th style={{ width: "9rem" }}>Eingebracht von</th>
                  <th style={{ width: "7rem" }}>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((poll) => (
                  <tr key={poll.id}>
                    <td style={{ whiteSpace: "nowrap", color: "var(--ink-2)" }}>
                      {formatDate(poll.poll_date)}
                    </td>
                    <td>
                      <Link to={`/abstimmungen/${poll.id}`} style={{ fontWeight: 500 }}>
                        {poll.label}
                      </Link>
                      <div
                        style={{
                          fontSize: "0.82rem",
                          color: "var(--ink-3)",
                          marginTop: "0.15rem",
                        }}
                      >
                        {poll.vorgang_typ ? (
                          <>
                            {poll.vorgang_typ}
                            {poll.beratungsstand ? ` · ${poll.beratungsstand}` : ""}
                          </>
                        ) : (
                          <em>keine Vorlage zugeordnet</em>
                        )}
                        {poll.committee && poll.committee.length > 0
                          ? ` · ${poll.committee.filter(Boolean).join(", ")}`
                          : ""}
                      </div>
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>
                      <Initiators urheber={poll.urheber} fallback="–" />
                    </td>
                    <td>
                      <span className={`badge ${poll.accepted ? "yes" : "no"}`}>
                        {poll.accepted ? "angenommen" : "abgelehnt"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={LIMIT} total={data.total} onChange={setOffset} />
        </>
      )}
    </>
  );
}
